"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { Check, Flag, Link2, ListPlus, Play, Plus, Square, Trash2 } from "lucide-react";
import { useReportShare } from "./report-share-provider";
import { usePiecePreview } from "@/hooks/use-piece-preview";
import { track } from "@/lib/analytics";
import {
  addToPlaylist,
  createPlaylist,
  removeFromPlaylist,
  type CatalogPiece,
  type LibraryEntryItem,
} from "@/lib/api/api-client";
import { libraryPlaylistPath } from "@/lib/library/library-routes";
import { MenuAction, MenuLabel, MenuSeparator } from "./actions-menu";
import { useLibraryFeedback } from "./library-feedback";
import { useLibraryOverview } from "./library-overview";
import { useLibraryActions } from "./use-library-actions";

/**
 * Le contenu du menu d'une carte : un seul composant pour les trois surfaces,
 * et un contenu qui dépend de ce que la carte permet.
 *
 * # Ce que chaque cas propose
 *
 * **Une carte de ma bibliothèque** : écouter, ranger dans une playlist, copier
 * le lien, retirer.
 *
 * **Une carte du catalogue absente de ma bibliothèque** : écouter, ajouter. Ni
 * playlist ni partage — les deux supposent une entrée, et proposer un geste qui
 * en créerait une en douce ferait entrer un morceau dans la bibliothèque sans
 * qu'on l'ait demandé.
 *
 * **Une carte du catalogue déjà présente** : écouter, copier le lien. Pas la
 * playlist, et c'est une limite de l'API assumée : les coches ont besoin de
 * `entry.playlistIds`, que `CatalogPiece` ne porte pas. Des cases vides sur un
 * morceau déjà rangé quelque part mentiraient, et une bascule qui ment est pire
 * qu'une entrée de menu absente. Le geste reste disponible depuis « Mes
 * morceaux », où l'entrée est là.
 *
 * # Le retrait est toujours en dernier, derrière un filet
 *
 * C'est la seule entrée qui enlève quelque chose. Au milieu des autres, elle
 * serait cliquée en visant sa voisine.
 *
 * # Les playlists sont une seconde page, pas un second popover
 *
 * Un popover ouvert depuis un popover empile deux calques, dont le premier se
 * ferme au clic extérieur — c'est-à-dire au clic dans le second. Le panneau
 * change donc de page sur place. La création d'une playlist est un champ posé
 * dans cette page, et non un dialogue : ouvrir une modale par-dessus deux
 * calques ferait perdre de vue le morceau qu'on était en train de ranger.
 *
 * Les coches ne coûtent aucun appel : `entry.playlistIds` arrive avec la ligne.
 */

type Props = {
  piece: CatalogPiece;
  /**
   * L'entrée de bibliothèque correspondante, `null` quand on ne l'a pas sous la
   * main. C'est elle qui apporte les coches des playlists et le retrait.
   */
  entry: LibraryEntryItem | null;
  /**
   * Le morceau est dans ma bibliothèque.
   *
   * Distinct de `entry !== null` : une carte de catalogue le sait par
   * `piece.inLibrary` sans avoir l'entrée. Par défaut, avoir l'entrée vaut
   * possession.
   */
  owned?: boolean;
  /** Ferme le menu. Appelé après chaque geste qui a un retour d'action. */
  close: () => void;
  /** Retire de la bibliothèque. Absent : l'entrée ne peut pas être retirée d'ici. */
  onRemove?: () => void;
  /**
   * Les entrées propres à la surface : « Monter », « Descendre » et « Retirer
   * de la playlist » sur une ligne de playlist. Rendues avant le filet.
   */
  extra?: React.ReactNode;
};

export function PieceMenu({
  piece,
  entry,
  owned = entry !== null,
  close,
  onRemove,
  extra,
}: Props) {
  const [page, setPage] = useState<"root" | "playlists">("root");

  if (page === "playlists" && entry !== null) {
    return <PlaylistPage entry={entry} close={close} onBack={() => setPage("root")} />;
  }

  return (
    <RootPage
      piece={piece}
      entry={entry}
      owned={owned}
      close={close}
      onRemove={onRemove}
      onPlaylists={() => setPage("playlists")}
      extra={extra}
    />
  );
}

function RootPage({
  piece,
  entry,
  owned,
  close,
  onRemove,
  onPlaylists,
  extra,
}: {
  piece: CatalogPiece;
  entry: LibraryEntryItem | null;
  owned: boolean;
  close: () => void;
  onRemove?: () => void;
  onPlaylists: () => void;
  extra?: React.ReactNode;
}) {
  /*
    Le lecteur d'aperçu vit ici, dans le panneau, et non dans le bouton qui
    l'ouvre : le panneau n'est monté qu'à l'ouverture, donc une grille de
    vingt-quatre cartes ne tient pas vingt-quatre lecteurs.
  */
  const preview = usePiecePreview(piece.id);
  const { busy, addToLibrary, copyShareLink } = useLibraryActions();
  // `null` hors de la bibliothèque : ce menu sert aussi des surfaces qui ne
  // montent pas le fournisseur, et où aucune entrée partagée n'apparaît.
  const report = useReportShare();

  return (
    <>
      {preview.status !== "unavailable" && (
        <MenuAction
          label={preview.status === "playing" ? "Arrêter l'extrait" : "Écouter un extrait"}
          icon={<Play size={15} strokeWidth={1.7} aria-hidden />}
          onSelect={() => {
            /*
              Le menu reste ouvert : l'extrait dure vingt secondes, et fermer
              le panneau retirerait le seul moyen de l'arrêter. `toggle` doit
              être appelé directement depuis le clic, c'est la condition pour
              que le navigateur autorise le son.
            */
            preview.toggle();
          }}
        />
      )}

      {entry !== null && (
        <MenuAction
          label="Ajouter à une playlist"
          icon={<ListPlus size={15} strokeWidth={1.7} aria-hidden />}
          onSelect={onPlaylists}
        />
      )}

      {owned && (
        <MenuAction
          label="Copier le lien de partage"
          icon={<Link2 size={15} strokeWidth={1.7} aria-hidden />}
          disabled={busy}
          onSelect={() => {
            /*
              La provenance vient de l'entrée quand on l'a. Sans entrée, ce menu
              est celui d'une carte de catalogue déjà rangée (`owned` par
              `piece.inLibrary`) : `catalog` n'est pas un défaut de confort, il
              décrit la seule situation où cette branche se produit.
            */
            copyShareLink(piece, entry?.source ?? "catalog", entry?.scoreId);
            close();
          }}
        />
      )}

      {!owned && (
        <MenuAction
          label="Ajouter à mes morceaux"
          icon={<Plus size={15} strokeWidth={1.7} aria-hidden />}
          disabled={busy}
          onSelect={() => {
            addToLibrary(piece);
            close();
          }}
        />
      )}

      {extra}

      {/*
          Signaler ne se propose que sur un morceau **reçu** : signaler ce qu'on
          a importé soi-même n'a pas de sens, et une pièce du catalogue relève
          de la procédure des mentions légales, pas de la modération d'un
          partage. C'est aussi le seul endroit où le signalement reste
          joignable une fois l'écran du lien oublié.
      */}
      {entry !== null && entry.source === "shared" && report !== null && (
        <MenuAction
          label="Signaler"
          icon={<Flag size={15} strokeWidth={1.7} aria-hidden />}
          tone="quiet"
          onSelect={() => {
            report.requestReport({ entryId: entry.id, pieceTitle: entry.title });
            close();
          }}
        />
      )}

      {entry !== null && onRemove !== undefined && (
        <>
          <MenuSeparator />
          <MenuAction
            label="Retirer de mes morceaux"
            icon={<Trash2 size={15} strokeWidth={1.7} aria-hidden />}
            tone="quiet"
            onSelect={() => {
              onRemove();
              close();
            }}
          />
        </>
      )}
    </>
  );
}

/**
 * La liste des playlists, cochée par `entry.playlistIds`.
 *
 * Le geste est une **bascule** : cocher range, décocher retire. Deux entrées
 * séparées auraient demandé de savoir d'avance si le morceau y est déjà, ce que
 * la coche dit justement.
 */
function PlaylistPage({
  entry,
  close,
  onBack,
}: {
  entry: LibraryEntryItem;
  close: () => void;
  onBack: () => void;
}) {
  const router = useRouter();
  const { playlists, refresh } = useLibraryOverview();
  const { notify } = useLibraryFeedback();
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  /*
    L'état local des coches. Il part de `entry.playlistIds`, puis suit les
    bascules : l'entrée, elle, ne sera relue qu'au prochain chargement de la
    liste, et attendre ce rechargement pour cocher donnerait un menu qui ne
    réagit pas au doigt.
  */
  const [members, setMembers] = useState<ReadonlySet<string>>(
    () => new Set(entry.playlistIds),
  );

  const toggle = useCallback(
    (playlistId: string, playlistName: string, pieceCount: number) => {
      const inside = members.has(playlistId);
      setBusy(true);

      setMembers((current) => {
        const next = new Set(current);

        if (inside) {
          next.delete(playlistId);
        } else {
          next.add(playlistId);
        }

        return next;
      });

      const call = inside
        ? removeFromPlaylist(playlistId, entry.scoreId)
        : addToPlaylist(playlistId, { scoreId: entry.scoreId }).then(() => undefined);

      call
        .then(() => {
          refresh();

          /*
            La bascule est un seul geste à deux sens : un événement paramétré,
            pas deux courbes. Émis après la réponse du serveur — la coche est
            optimiste et se défait si l'appel échoue, la mesure ne compte que
            ce qui a tenu. `size` est la taille de la liste avant le geste,
            celle que la carte du répertoire affichait au moment du clic.
          */
          track({
            name: "playlist_action",
            properties: {
              action: inside ? "piece_removed" : "piece_added",
              size: pieceCount,
            },
          });

          if (inside) {
            notify({ message: `Retiré de « ${playlistName} ».` });

            return;
          }

          notify({
            message: `Ajouté à « ${playlistName} ».`,
            action: {
              label: "Voir",
              onAct: () => router.push(libraryPlaylistPath(playlistId)),
            },
          });
        })
        .catch(() => {
          setMembers((current) => {
            const next = new Set(current);

            if (inside) {
              next.add(playlistId);
            } else {
              next.delete(playlistId);
            }

            return next;
          });
          notify({ message: "La playlist n'a pas pu être modifiée.", failed: true });
        })
        .finally(() => setBusy(false));
    },
    [members, entry.scoreId, refresh, notify, router],
  );

  const create = useCallback(() => {
    const trimmed = name.trim();

    if (trimmed === "" || busy) {
      return;
    }

    setBusy(true);

    createPlaylist(trimmed)
      .then((playlist) => addToPlaylist(playlist.id, { scoreId: entry.scoreId }).then(() => playlist))
      .then((playlist) => {
        refresh();
        /*
          Créée **avec** son premier morceau : c'est un seul geste, donc un
          seul événement, et `size: 1` dit que la pièce y est entrée. L'émettre
          en plus comme `piece_added` compterait deux fois le même clic.
        */
        track({
          name: "playlist_action",
          properties: { action: "created", size: 1 },
        });
        notify({
          message: `Ajouté à « ${playlist.name} ».`,
          action: {
            label: "Voir",
            onAct: () => router.push(libraryPlaylistPath(playlist.id)),
          },
        });
        close();
      })
      .catch(() => {
        notify({
          // `409` sur un nom déjà pris : ils sont uniques par personne.
          message: "Ce nom est déjà pris, ou la playlist n'a pas pu être créée.",
          failed: true,
        });
      })
      .finally(() => setBusy(false));
  }, [name, busy, entry.scoreId, refresh, notify, router, close]);

  return (
    <>
      <MenuLabel>Ajouter à une playlist</MenuLabel>

      {playlists.length === 0 && !creating && (
        <p className="px-2.5 pb-2 text-[12px] leading-snug text-text-muted">
          Tu n&apos;as pas encore de playlist.
        </p>
      )}

      <div className="max-h-[220px] overflow-y-auto">
        {playlists.map((playlist) => (
          <MenuAction
            key={playlist.id}
            label={playlist.name}
            disabled={busy}
            icon={
              members.has(playlist.id) ? (
                <Check
                  size={15}
                  strokeWidth={2}
                  aria-hidden
                  style={{ color: "var(--color-accent-bright)" }}
                />
              ) : (
                <Square size={15} strokeWidth={1.4} aria-hidden />
              )
            }
            onSelect={() => toggle(playlist.id, playlist.name, playlist.pieceCount)}
          />
        ))}
      </div>

      <MenuSeparator />

      {creating ? (
        <div className="px-2 pb-1.5">
          <label
            htmlFor="new-playlist-name"
            className="block pb-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] text-text-muted"
          >
            Nom
          </label>
          <input
            id="new-playlist-name"
            autoFocus
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                create();
              }
            }}
            placeholder="Pour le dimanche matin"
            className="h-10 w-full rounded-lg border border-border-subtle bg-white/[0.04] px-2.5 text-[13px] text-text-primary placeholder:text-text-muted focus:border-accent/40 focus:outline-none focus:ring-1 focus:ring-accent/15"
          />
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onBack}
              className="h-8 cursor-pointer rounded-lg px-3 text-[12.5px] text-text-secondary transition-colors duration-150 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={create}
              disabled={busy || name.trim() === ""}
              className="h-8 cursor-pointer rounded-lg px-3 text-[12.5px] font-semibold text-text-primary transition-all duration-150 disabled:cursor-default disabled:opacity-40"
              style={{ backgroundColor: "var(--color-accent)" }}
            >
              Créer
            </button>
          </div>
        </div>
      ) : (
        <MenuAction
          label="Nouvelle playlist"
          icon={<Plus size={15} strokeWidth={1.7} aria-hidden />}
          onSelect={() => setCreating(true)}
        />
      )}
    </>
  );
}
